/**
 * Browser-safe: the contract and the console read a filing, so nothing from
 * `node:` or the database belongs here.
 */

import { z } from 'zod';
import { INTERVAL_MAX_LENGTH, STRATEGY_MAX_LENGTH } from './setup.ts';

export const FILING_SOURCES = ['ADD', 'TELEGRAM', 'TRADINGVIEW'] as const;

/** What is too long is cut, never summarised. */
const FILED_TEXT_LIMIT = 2000;

export const excerpt = (text: string): string =>
  text.length > FILED_TEXT_LIMIT ? `${text.slice(0, FILED_TEXT_LIMIT)}…` : text;

export const closedSaidSchema = z.object({
  text: z.string().trim().min(1).transform(excerpt),
  link: z.url({ protocol: /^https$/ }).nullable(),
});

export type ClosedSaid = z.infer<typeof closedSaidSchema>;

const setupSchema = z.object({
  strategy: z.string().trim().min(1).max(STRATEGY_MAX_LENGTH),
  interval: z.string().trim().min(1).max(INTERVAL_MAX_LENGTH).nullable().default(null),
});

/**
 * The words are the recommendation's own copy, not a pointer: the post archive
 * belongs to the analyst and goes when they do, and a draft is settled and
 * forgotten, so neither can be shown beside the recommendation years later.
 */
export const filingSchema = z.discriminatedUnion('source', [
  z.object({ source: z.literal('ADD') }),
  z.object({
    source: z.literal('TELEGRAM'),
    link: z.string().min(1).nullable(),
    /** Empty on a picture-only post. */
    text: z.string().max(FILED_TEXT_LIMIT),
    /** When it was posted, which is not when it was filed. */
    at: z.date(),
  }),
  z.object({
    source: z.literal('TRADINGVIEW'),
    text: z.string().max(FILED_TEXT_LIMIT),
    /** When the alert reached the desk, which is not when it was filed. */
    at: z.date(),
    setup: setupSchema.nullable().default(null),
  }),
]);

export type Filing = z.infer<typeof filingSchema>;

export const TYPED_IN: Filing = { source: 'ADD' };

export function filingColumns(filing: Filing) {
  return {
    source: filing.source,
    sourceLink: filing.source === 'TELEGRAM' ? filing.link : null,
    sourceText: filing.source === 'ADD' ? null : filing.text,
    sourceAt: filing.source === 'ADD' ? null : filing.at,
    setupStrategy: filing.source === 'TRADINGVIEW' ? (filing.setup?.strategy ?? null) : null,
    setupInterval: filing.source === 'TRADINGVIEW' ? (filing.setup?.interval ?? null) : null,
  };
}
